import React, { Component } from 'react';
import { connectDeep } from 'redux-utils';
import PropTypes from 'prop-types';
import RFRLink from 'redux-first-router-link';
import styled from 'styled-components';
import raf from 'raf';
import GearIcon from 'icons/gear.svg';
import store from 'store';
import { direction } from 'actions/gallery';
import ScrollContainer from 'redux-first-router-scroll-container';
import { isiOS } from 'config';

export const FULL_VH = document.documentElement.clientHeight;
export const FULL_VW = document.documentElement.clientWidth;

const APPS_CLOCKS = ['GALLERY_V2_CLOCKS', 'GALLERY_V2_APPS'];
const APPS_TILES = ['GALLERY_V2_APPS', 'GALLERY_V2_TILES'];
const CLOCKS_TILES = ['GALLERY_V2_CLOCKS', 'GALLERY_V2_TILES'];

const shouldUpdateScroll = (prev: any, next: any) => {
  if (prev &&
      next &&
      ((next.type === 'GALLERY_V2_CLOCKS' && APPS_TILES.includes(prev.type)) ||
      (next.type === 'GALLERY_V2_APPS' && CLOCKS_TILES.includes(prev.type)) ||
      (next.type === 'GALLERY_V2_TILES' && APPS_CLOCKS.includes(prev.type)))
  ) {
    return [0, 0];
  }

  // Don't update scroll if we are pushing a new page onto the history stack
  if (next && next.kind === 'push') {
    return false;
  }

  // Otherwise, if popping (forward/back), update scroll
  return true;
};

export const BasicContentBlock = styled.div<any>`
  ${({ theme, borderTop, borderBottom, paddingLeft = true, paddingRight = true }) => `
    background-color: var(--sys-color--surface);
    padding-left: ${paddingLeft ? theme.edgeDistance : 'none'};
    padding-right: ${paddingRight ? theme.edgeDistance : 'none'};
    border-top: ${borderTop ? `1px solid ${theme.borderColorV2}` : 'none'};
    border-bottom: ${borderBottom ? `1px solid ${theme.borderColorV2}` : 'none'};
  `}
`;

export const ContentBlock = styled(BasicContentBlock)<any>`
  ${({ paddingTop = true, paddingBottom = true, lineHeight = true }) => `
    padding-top: ${paddingTop ? '20px' : 'none'};
    padding-bottom: ${paddingBottom ? '20px' : 'none'};
  
    font-size: 16px;
    line-height: ${lineHeight ? '24px' : 'none'};
  `}
`;

export const Icon = `
  height: auto;
  width: 48px;
`;

export const SectionTitle = styled.div<any>`
  ${({ theme, nearRightEdge = false }) => `
    display: flex;
    margin: 0;
    padding-left: ${nearRightEdge ? 'none' : theme.edgeDistance};
    padding-right: ${nearRightEdge ? theme.edgeDistance : 'none'};
    color: #707C83;
    background-color: #F6F6F6;
    height: 40px;
    align-items: flex-end;
    padding-bottom: 10px;
  `}
`;

export const SettingsIcon = styled.div`
  background-image: url(${GearIcon});
  background-size: contain;
  cursor: pointer;
  width: 22px;
  height: 22px;
  -webkit-transform: translateZ(0);
`;

export const StyledRFRLink = styled(RFRLink)`
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

export const ModalGrayText = styled.div`
  font-size: 16px;
  line-height: 24px;

  text-align: center;
  letter-spacing: -0.32px;

  color: #707C83;
`;

export const StyledLink = styled.a`
  color: green;
`;

export const CustomizeButton = styled.button`
  cursor: pointer;
  background: #112530;
  border-radius: 16px;

  color: white;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  min-width: 140px;
  min-height: 50px;
  border-color: #000000;
`;

export const CustomizeButtonContainer = styled.div`
  display: flex;
`;

export const CustomizeButtonTextContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 4px;

  font-size: 14px;
  line-height: 24px;

  letter-spacing: -0.28px;
  text-transform: uppercase;

  color: #FFFFFF;

  flex: none;
  order: 1;
  align-self: center;
  flex-grow: 0;
  margin: 0px 8px;
`;

export const CustomizeSign = styled.img`
  margin-right: -8px;
  height: 17px;
  margin-top: 2px;
`;

interface LinkProps {
  pageDirection: string;
  onClick?: () => void;
  to?: string;
  id?: string;
}
export class Link extends Component<LinkProps> {
  static propTypes = {
    pageDirection: PropTypes.string,
    onClick: PropTypes.func
  };

  onClick = () => {
    const { pageDirection, onClick } = this.props;
    store.dispatch(direction(pageDirection));
    if (onClick) {
      onClick();
    }
  };

  render() {
    // eslint-disable-next-line
    const { children, to, id } = this.props;
    return (
      <StyledRFRLink to={to} id={id} aria-label={id} onClick={this.onClick}>
        {children}
      </StyledRFRLink>
    );
  }
}

export const animationFixStyle = `
  -webkit-backface-visibility: hidden;
  -webkit-transform-style: preserve-3d;
`;

const scrollThreshold = 10;

const DisconnectedPage = styled(
  class extends Component<any, any> {
    private ref: HTMLDivElement
    private rafHandle?: any

    onRef = (ref: HTMLDivElement) => {
      this.ref = ref;
      if (this.ref) {
        this.ref.addEventListener('scroll', this.handleScroll);
        this.ref.addEventListener('touchmove', this.handleScroll);
      }
    };

    handleScroll = () => {
      this.rafHandle = raf(() => {
        this.rafHandle = undefined;
        if (!this.ref) {
          return;
        }

        const withinThreshold =
          this.ref.scrollTop > scrollThreshold &&
          this.ref.scrollTop < this.ref.clientHeight - scrollThreshold;
        let top = this.ref.scrollTop;
        if (this.ref.scrollTop <= scrollThreshold) {
          top = 0;
        } else if (
          this.ref.scrollTop >=
          this.ref.clientHeight - scrollThreshold
        ) {
          top = this.ref.clientHeight;
        }

        this.props.onScroll({
          scrolling: withinThreshold,
          top
        });
      });
    };

    componentWillUmmount() {
      this.ref.removeEventListener('scroll', this.handleScroll);
      this.ref.removeEventListener('touchmove', this.handleScroll);

      if (this.rafHandle) {
        this.rafHandle.cancel();
        this.rafHandle = undefined;
      }
    }

    render() {
      const { className, children, scrollKey } = this.props;

      const scrollEl = (
        <div ref={this.onRef} className={className}>
          {children}
        </div>
      );

      return scrollKey ? (
        <ScrollContainer
          scrollKey={scrollKey}
          shouldUpdateScroll={shouldUpdateScroll}
        >
          {scrollEl}
        </ScrollContainer>
      ) : (
        scrollEl
      );
    }
  }
)`
  box-sizing: border-box;
  height: calc(${FULL_VH}px - ${({ navbarHeight }) => navbarHeight});
  margin-top: ${({ navbarHeight }) => navbarHeight};
  position: relative;
  background-color: var(--sys-color--surface);
  // Force GPU Accel
  transform: translate3d(0, 0, 0);
  ${({ modalShowing = false, theme }) => `
    overflow-y: ${isiOS && modalShowing ? 'hidden' : 'auto'};
    overflow-x: hidden;
    ${theme.Page}
  `}
`;

const mapStateToProps = ({ gallery: { modalShowing } }: { gallery: { modalShowing: boolean}}) => ({
  modalShowing
});

export const Page = connectDeep(mapStateToProps)(DisconnectedPage);
